import { AdjustmentsService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, ButtonDropdown, ButtonDropdownProps, Container, Link, SpaceBetween } from '@amzn/awsui-components-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { SplitPanelContext } from '../../../contexts/SplitPanelContext';
import { TcoContext } from '../../../contexts/TcoContext';
import { convertDBAdjustmentToDisplay, getTitleToDisplay } from './adjustments-helper';
import { ActionOptions, getAdjustmentColumns } from './constants';
import DeleteAdjustmentsModal from './DeleteAdjustmentsModal';
import { AdjustmentItem, AdjustmentType } from './interfaces';
import ViewAdjustmentDetails from './ViewAdjustmentDetails';

type Props = FlashContextInterface;

const adjustmentsService = new AdjustmentsService();
const tcoService = new TcoService();

function ViewAdjustments({ flash }: Props): JSX.Element | null {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });

    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;

    const { alert } = flash;
    const history = useHistory();
    const [ loading, setLoading ] = useState(false);
    const [ adjustmentItems, setAdjustmentItems ] = useState<AdjustmentItem[]>([]);
    const [ selectedAdjustments, setSelectedAdjustments ] = useState<AdjustmentItem[]>([]);
    const [ currency, setCurrency ] = useState('');
    const [ tcoDurationYears, setTcoDurationYears ] = useState(0);
    const [ showDeleteConfirmationModal, setShowDeleteConfirmationModal ] = useState(false);

    const { closePanel, openPanel, setPanelContent } = useContext(SplitPanelContext);
    const { assessment, tcoLoading } = useContext(TcoContext);
    const { panelHandler } = useContext(HelpPanelContext);
    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ADJUSTMENTS_VIEW);
    }, [ panelHandler ]);
    
    const loadAdjustments = useCallback( async () => {
        if (!portfolioId || !tcoAssessmentId || tcoAssessmentId === 'default' || !assessment) {
            return;
        }
        setLoading(true);
        try {
            const tcoSummary = await tcoService.getSummary(tcoAssessmentId);
            setCurrency(tcoSummary.output.summary.currency);
            setTcoDurationYears(tcoSummary.configuration.tcoAssumptions.durationYears);

            const adjustments = await adjustmentsService.list(portfolioId, tcoAssessmentId);
            const adjustmentsToDisplay = convertDBAdjustmentToDisplay(adjustments);
            setAdjustmentItems(adjustmentsToDisplay);
        } catch (err) {
            alert(FlashType.error ,(err as ErrorWithResponse).message, constants.ERROR_LOADING_ADJUSTMENTS);
        } finally {
            setLoading(false);
        }
    }, [ portfolioId, tcoAssessmentId, alert, assessment ]);

    useEffect(() => {
        loadAdjustments();
    }, [ loadAdjustments ]);

    const addBtnClick = () => {
        history.push(`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments/add`);
    };

    const getEditPath = useCallback((id: string): string => {
        return `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments/edit/${id}`;
    },[portfolioId, tcoAssessmentId]);

    const actionSelected = (action: string): void => {
        switch(action) {
            case ActionOptions.EDIT:
                history.push(getEditPath(selectedAdjustments[0].adjustmentId));
                return;
            case ActionOptions.DELETE:
                setShowDeleteConfirmationModal(true);
                return;
        }
    };

    const actionDropdownOptions: ButtonDropdownProps.ItemOrGroup[] = useMemo(() => [
        {
            text: constants.EDIT_BUTTON_TEXT,
            id: ActionOptions.EDIT,
            disabled: selectedAdjustments?.length !== 1
        },
        {
            text: constants.DELETE_BUTTON_TEXT,
            id: ActionOptions.DELETE,
            disabled: selectedAdjustments?.length === 0
        }
    ], [selectedAdjustments?.length]);

    const onSelectionChanged = (items: readonly AdjustmentItem[] | undefined) => {
        setSelectedAdjustments(items? [...items] : []);
        if (items?.length === 1) {
            const selectedAdjustment = items[0];
            const matchingAdjustment = 
                adjustmentItems.find(adjustment => adjustment.adjustmentId === selectedAdjustment.adjustmentId && adjustment.type !== selectedAdjustment.type);
            const header = !matchingAdjustment || getTitleToDisplay(selectedAdjustment) === getTitleToDisplay(matchingAdjustment)
                ? getTitleToDisplay(selectedAdjustment) : `${getTitleToDisplay(selectedAdjustment)}, ${getTitleToDisplay(matchingAdjustment)}`;
            setPanelContent(
                <ViewAdjustmentDetails
                    currency={currency}
                    awsAdjustment={selectedAdjustment.type === AdjustmentType.AWS ? selectedAdjustment : matchingAdjustment}
                    onPremAdjustment={selectedAdjustment.type === AdjustmentType.ON_PREMISES ? selectedAdjustment : matchingAdjustment}
                />, header);
            openPanel();
        } else {
            closePanel();
            setPanelContent(undefined, '');
        }
    };

    const displayEditLink = useCallback((text: string, id: string): JSX.Element => {
        return <Link onFollow={() => history.push(getEditPath(id))}>{text}</Link>;
    }, [getEditPath, history]);

    const columnDefs = useMemo(() =>  getAdjustmentColumns(currency, tcoDurationYears, displayEditLink), [currency, displayEditLink, tcoDurationYears]);

    return tcoAssessmentId && tcoAssessmentId !== 'default' ? (
        <SpaceBetween size='s'>
            <Container>
                <AWSCATTable
                    resourceName={constants.ADJUSTMENTS}
                    items={adjustmentItems}
                    columnDefinitions={columnDefs}
                    loading={loading || tcoLoading}
                    selectionType='multi'
                    onSelectionChange={onSelectionChanged}
                    useDefaultDownload
                    resizeable
                    autoGenerateVisibleContent
                    enableAutoSorting
                    headerActions={
                        <SpaceBetween size='m' direction='horizontal'>
                            <Button iconName='refresh' onClick={loadAdjustments} loading={loading} />
                            <ButtonDropdown
                                items={actionDropdownOptions}
                                onItemClick={({ detail }) => actionSelected(detail.id)}
                            >
                                {constants.ACTION_BUTTON_TEXT}
                            </ButtonDropdown>
                            <Button
                                onClick={addBtnClick}
                                variant='primary'
                            >
                                {constants.ADD_BUTTON_TEXT}
                            </Button>
                        </SpaceBetween>
                    }
                />
            </Container>
            {showDeleteConfirmationModal && (
                <DeleteAdjustmentsModal 
                    selectedAdjustments={selectedAdjustments}
                    allAdjustments={adjustmentItems}
                    dismissModal={() => setShowDeleteConfirmationModal(false)}
                    refreshData={loadAdjustments}
                />
            )}
        </SpaceBetween>
    ) : null;
}

export default withFlashContext(ViewAdjustments);
